import { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
    CheckCircle2, Circle, Loader2, MapPin, Calendar, Play, Mail, ArrowRight, ShieldCheck, CreditCard,
} from "lucide-react";
import { NoxnLogo } from "@/components/noxn/NoxnLogo";
import { usePageMeta } from "@/hooks/usePageMeta";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const INK = "#1E3A6F";
const ACCENT = "#FF5A00";

const STAGES = [
    { key: "new",       label: "Received",  hint: "We have your booking and are matching a screen." },
    { key: "approved",  label: "Approved",  hint: "Slot is reserved. Payment link on the way." },
    { key: "scheduled", label: "Scheduled", hint: "Your artwork is queued for launch." },
    { key: "live",      label: "Live",      hint: "Your billboard is playing right now." },
    { key: "done",      label: "Delivered", hint: "Campaign complete. Play log emailed." },
];

const stageIndex = (s) => {
    const i = STAGES.findIndex((x) => x.key === s);
    return i === -1 ? 0 : i;
};

const dollars = (c) => (c == null ? "—" : `$${(c / 100).toFixed(2)}`);

export default function GuestCampaign() {
    usePageMeta({ title: "Your campaign · NOXN", description: "Track your NOXN billboard booking." });
    const { token } = useParams();
    const [params] = useSearchParams();
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);
    const [payLoading, setPayLoading] = useState(false);

    const load = () => {
        if (!token) return;
        axios.get(`${API}/guest/campaign/${token}`)
            .then(({ data }) => setData(data))
            .catch((e) => setError(e?.response?.data?.detail || "Campaign not found."));
    };

    useEffect(() => {
        load();
        // Stripe just redirected the guest back with paid=1 — poll briefly
        // in case the webhook hasn't landed yet.
        if (params.get("paid") === "1") {
            toast.success("Payment complete — you're booked.");
            const t = setInterval(() => {
                axios.get(`${API}/guest/campaign/${token}`)
                    .then(({ data }) => {
                        setData(data);
                        if (data.payment_status === "paid") clearInterval(t);
                    })
                    .catch(() => { });
            }, 1500);
            setTimeout(() => clearInterval(t), 15000);
        } else if (params.get("paid") === "0") {
            toast.info("Payment cancelled. Your slot is held — you can retry any time.");
        }
    }, [token]);  // eslint-disable-line react-hooks/exhaustive-deps

    const payNow = async () => {
        setPayLoading(true);
        try {
            const { data: chk } = await axios.post(
                `${API}/guest/campaign/${token}/checkout`,
                { origin_url: window.location.origin },
            );
            if (chk.checkout_url) window.location.href = chk.checkout_url;
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not start checkout.");
            setPayLoading(false);
        }
    };

    const active = stageIndex(data?.pipeline_status);
    const paid = data?.payment_status === "paid";
    const totalCents = data?.plays && data?.price_per_play_cents ? data.plays * data.price_per_play_cents : 0;
    const gstCents = Math.round(totalCents * 0.10);
    const grandCents = totalCents + gstCents;

    return (
        <div className="min-h-screen bg-white font-body" style={{ color: INK }} data-testid="guest-campaign-page">
            <header className="fixed inset-x-0 top-0 z-40 bg-white/85 backdrop-blur border-b" style={{ borderColor: `${INK}12` }}>
                <div className="mx-auto max-w-4xl px-6 lg:px-10 h-16 flex items-center justify-between">
                    <Link to="/" className="flex items-center"><NoxnLogo variant="navy" className="h-5 w-auto" /></Link>
                    <Link to="/book" className="text-[13px] hover:underline" style={{ color: `${INK}99` }}>Book another →</Link>
                </div>
            </header>

            <main className="mx-auto max-w-4xl px-6 lg:px-10 pt-32 pb-20">
                {!data && !error && (
                    <div className="flex items-center gap-2 text-sm" style={{ color: `${INK}80` }} data-testid="guest-loading">
                        <Loader2 size={14} className="animate-spin" /> Loading your campaign…
                    </div>
                )}
                {error && (
                    <div className="max-w-lg" data-testid="guest-error">
                        <h1 className="font-display text-3xl font-medium mb-3" style={{ color: INK }}>Not found</h1>
                        <p className="text-[15px]" style={{ color: `${INK}80` }}>{error}</p>
                        <Link to="/book" className="mt-6 inline-flex items-center gap-2 rounded-full py-3 px-6 text-sm text-white" style={{ backgroundColor: INK }}>
                            Start a new booking <ArrowRight size={14} />
                        </Link>
                    </div>
                )}
                {data && (
                    <>
                        <motion.div
                            initial={{ opacity: 0, y: 14 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
                            className="mb-10"
                        >
                            <div className="text-[11px] uppercase tracking-[0.28em] mb-3 inline-flex items-center gap-2" style={{ color: `${INK}70` }}>
                                <motion.span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: ACCENT }}
                                    animate={{ opacity: [1, 0.35, 1] }} transition={{ duration: 2, repeat: Infinity }} />
                                Booking · {data.id.slice(0, 8)}
                            </div>
                            <h1 className="font-display tracking-[-0.03em] text-4xl sm:text-5xl leading-[1.05] font-medium" style={{ color: INK }} data-testid="guest-heading">
                                {data.business_name || "Your billboard"} · Australia
                            </h1>
                            <p className="mt-4 text-[15px]" style={{ color: `${INK}80` }}>
                                Bookmark this page — we&apos;ll also email you at every stage change.
                                {data.email && <> A confirmation is on the way to <strong>{data.email}</strong>.</>}
                            </p>
                        </motion.div>

                        {/* Progress tracker */}
                        <div className="rounded-3xl border p-6 lg:p-8 mb-10" style={{ borderColor: `${INK}18`, backgroundColor: "#FAFBFD" }} data-testid="guest-tracker">
                            <div className="text-[11px] uppercase tracking-[0.28em] mb-6" style={{ color: `${INK}70` }}>Progress</div>
                            <ol className="space-y-4">
                                {STAGES.map((s, i) => {
                                    const done = i < active || (data.pipeline_status === "done" && s.key === "done");
                                    const cur = i === active;
                                    return (
                                        <motion.li
                                            key={s.key}
                                            initial={{ opacity: 0, x: -8 }}
                                            animate={{ opacity: 1, x: 0 }}
                                            transition={{ duration: 0.5, delay: i * 0.08 }}
                                            className="flex items-start gap-4"
                                            data-testid={`guest-stage-${s.key}`}
                                        >
                                            <div className="mt-0.5 shrink-0">
                                                {done || (cur && paid) ? (
                                                    <CheckCircle2 size={20} style={{ color: ACCENT }} />
                                                ) : cur ? (
                                                    <motion.div animate={{ scale: [1, 1.15, 1] }} transition={{ duration: 1.6, repeat: Infinity }}>
                                                        <Circle size={20} strokeWidth={2.5} style={{ color: ACCENT }} />
                                                    </motion.div>
                                                ) : (
                                                    <Circle size={20} style={{ color: `${INK}30` }} />
                                                )}
                                            </div>
                                            <div className="flex-1">
                                                <div className="font-display text-lg font-medium" style={{ color: done || cur ? INK : `${INK}55` }}>
                                                    {s.label}
                                                </div>
                                                <div className="text-[13px] mt-0.5" style={{ color: `${INK}75` }}>{s.hint}</div>
                                            </div>
                                            {cur && (
                                                <div className="hidden sm:block text-[11px] font-mono-tech uppercase tracking-[0.24em] px-2.5 py-1 rounded-full" style={{ backgroundColor: `${ACCENT}18`, color: ACCENT }}>
                                                    Now
                                                </div>
                                            )}
                                        </motion.li>
                                    );
                                })}
                            </ol>
                        </div>

                        {/* Details */}
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-10">
                            <Card icon={MapPin} k="Location" v={data.screen_name ? `${data.screen_name} · ${data.screen_city}` : (data.cities || []).join(", ") || "Matching screens"} />
                            <Card icon={Calendar} k="Launch date" v={data.launch_date || "TBC"} />
                            <Card icon={Play} k="Plays" v={data.plays ? `${data.plays.toLocaleString()} @ ${dollars(data.price_per_play_cents)}/play` : "—"} />
                        </div>

                        {/* Payment */}
                        {!paid && (
                            <div className="rounded-3xl p-6 lg:p-8 mb-10" style={{ backgroundColor: INK, color: "#FFFFFF" }} data-testid="guest-payment-cta">
                                <div className="flex flex-col md:flex-row md:items-center gap-6">
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-baseline gap-3 mb-2">
                                            <div className="text-[11px] uppercase tracking-[0.28em]" style={{ color: "#FFFFFF99" }}>Pay to reserve</div>
                                            <motion.span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: ACCENT }}
                                                animate={{ opacity: [1, 0.35, 1] }} transition={{ duration: 2, repeat: Infinity }} />
                                        </div>
                                        <div className="font-display text-3xl sm:text-4xl font-medium leading-[1.05]" data-testid="guest-total">
                                            ${(grandCents / 100).toFixed(2)}{" "}
                                            <span className="text-base font-normal" style={{ color: "#FFFFFF80" }}>incl. GST</span>
                                        </div>
                                        <p className="mt-3 text-[13px]" style={{ color: "#FFFFFFB0" }}>
                                            Secure Stripe checkout — pay by card, Google Pay, or Apple Pay. We&apos;ll email your receipt
                                            {data.email && <> to <strong style={{ color: "#FFFFFF" }}>{data.email}</strong></>} the moment it clears.
                                        </p>
                                    </div>
                                    <button
                                        onClick={payNow}
                                        disabled={payLoading}
                                        data-testid="guest-pay-now"
                                        className="group inline-flex items-center gap-2 rounded-full px-7 py-4 text-sm font-medium transition-all disabled:opacity-60"
                                        style={{ backgroundColor: ACCENT, color: "#FFFFFF" }}
                                    >
                                        {payLoading
                                            ? <><Loader2 size={16} className="animate-spin" /> Loading…</>
                                            : <><CreditCard size={16} /> Pay ${(grandCents / 100).toFixed(2)} now <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" /></>
                                        }
                                    </button>
                                </div>
                                <div className="mt-5 flex items-start gap-2 text-[12px]" style={{ color: "#FFFFFFA0" }}>
                                    <Mail size={13} className="mt-0.5 shrink-0" />
                                    Prefer email? Reply to any NOXN thread and we&apos;ll send a payment link — same secure Stripe page.
                                </div>
                            </div>
                        )}

                        {paid && (
                            <div className="rounded-3xl p-6 lg:p-8 mb-10 border" style={{ borderColor: `${ACCENT}40`, backgroundColor: `${ACCENT}0d` }} data-testid="guest-payment-confirmed">
                                <div className="flex items-start gap-3">
                                    <CheckCircle2 size={18} className="mt-1" style={{ color: ACCENT }} />
                                    <div>
                                        <div className="font-display text-xl font-medium" style={{ color: INK }}>Payment received — you&apos;re booked.</div>
                                        <p className="mt-2 text-[14px]" style={{ color: `${INK}90` }}>
                                            Receipt emailed. Watch your inbox for the artwork checklist and the go-live confirmation.
                                        </p>
                                    </div>
                                </div>
                            </div>
                        )}

                        <div className="flex items-start gap-2 text-[13px]" style={{ color: `${INK}75` }}>
                            <ShieldCheck size={14} className="mt-0.5 shrink-0" style={{ color: ACCENT }} />
                            <span>
                                Keep this URL private — anyone with the link can see the booking status. Need help?{" "}
                                <a href="mailto:noxn@noxn.com.au" className="underline">noxn@noxn.com.au</a>
                            </span>
                        </div>
                    </>
                )}
            </main>
        </div>
    );
}

const Card = ({ icon: Icon, k, v }) => (
    <div className="rounded-2xl border p-5" style={{ borderColor: `${INK}18` }}>
        <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.28em] mb-2" style={{ color: `${INK}70` }}>
            <Icon size={13} style={{ color: ACCENT }} /> {k}
        </div>
        <div className="font-display text-lg font-medium" style={{ color: INK }}>{v}</div>
    </div>
);
